import {
  Body,
  H5,
  Link,
  Stack,
  useModals,
  useTheme,
  useWindowProperties,
} from '@qred/components-library';
import { useFeatureFlagVariantKey, usePostHog } from 'posthog-js/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import SkipButton from '~/components/shared/Stepper/components/SkipButton';
import { StepStatus } from '~/enums';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { StepConfigItemComponentProps } from '~/Interfaces';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { patchApplication } from '~/store/actions/loanApplicationActions';
import { useGetIndicativeOfferQuery } from '~/store/apis/endpoints/onboardingApi/onboarding.api';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  AnimatedWrapper,
  CheckpointContent,
  CheckpointPage,
  ContentCard,
  LoadingAnimation,
} from './Checkpoint.styles';
import { IndicativeOffer } from './components/IndicativeOffer';
import { UniqueSellingPoints } from './components/UniqueSellingPoints';
import { UniqueSellingPointsModal } from './components/UniqueSellingPointsModal';
import { VerticalTimeline } from './components/VerticalTimeline';

const MINIMUM_INDICATIVE_OFFER_LOADING_TIME_MS = 3000;

export const Checkpoint = ({
  setBackgroundOverride,
}: StepConfigItemComponentProps) => {
  const theme = useTheme();
  const t = useTranslate();
  const postHog = usePostHog();

  const {
    apiStatus,
    form: { applicationUuid },
  } = useSelector((state: RootState) => state.loanApplication);

  const indicativeOfferVariant = useFeatureFlagVariantKey(
    'AB_Web_LoanApplication_IndicativeOffer_MoreSuperApplications_SE_FI'
  );
  const skipIndicativeOfferFetch = indicativeOfferVariant !== 'test';
  const { openModal, closeModal } = useModals();

  const dispatch = useAppDispatch();
  const [buttonClicked, setButtonClicked] = useState<
    'BoostApplication' | 'CompleteLater'
  >();
  const { isMobile } = useWindowProperties();
  const {
    isFetching,
    data,
    startedTimeStamp,
    isError,
    isSuccess,
  } = useGetIndicativeOfferQuery(
    { applicationUuid },
    { skip: skipIndicativeOfferFetch }
  );

  // Calculate if minimum 3 seconds have elapsed since query started
  // If query completes (success or error) before 3 seconds, wait for 3 seconds
  // If query takes longer than 3 seconds, show result immediately when done
  // If data already exists on mount, mark as done immediately
  const [minTimeElapsed, setMinTimeElapsed] = useState(Boolean(data));

  useEffect(() => {
    if (startedTimeStamp && !minTimeElapsed) {
      const elapsed = Date.now() - startedTimeStamp;
      const remaining = Math.max(
        0,
        MINIMUM_INDICATIVE_OFFER_LOADING_TIME_MS - elapsed
      );

      const timer = setTimeout(() => {
        setMinTimeElapsed(true);
      }, remaining);

      return () => clearTimeout(timer);
    }

    return undefined;
  }, [startedTimeStamp, minTimeElapsed]);

  const minLoadingTimeElapsed = !isFetching && minTimeElapsed;

  useEffect(() => {
    if (
      indicativeOfferVariant &&
      (data?.showIndicativeOffer || indicativeOfferVariant === 'control')
    ) {
      postHog.capture('experiment_exposure', {
        experiment:
          'Web - Loan application - Indicative offer - More super applications - SE & FI',
        variant: indicativeOfferVariant,
      });
    }
  }, [data?.showIndicativeOffer, indicativeOfferVariant, postHog]);

  useEffect(() => {
    if (isSuccess) {
      if (data?.showIndicativeOffer) {
        dispatch(
          pushToGtmLoanAction({
            actionName: 'indicative_offer_fetch_success',
          })
        );
      } else {
        dispatch(
          pushToGtmLoanAction({
            actionName: 'indicative_offer_fetch_success_with_no_data',
          })
        );
      }
    } else if (isError) {
      dispatch(
        pushToGtmLoanAction({
          actionName: 'indicative_offer_fetch_error',
        })
      );
    }
  }, [isSuccess, isError]);

  useEffect(() => {
    setBackgroundOverride?.(theme.fn.themeColor('Page/Option_2_(Green)'));

    return () => {
      setBackgroundOverride?.(undefined);
    };
  }, [setBackgroundOverride, theme]);

  const handleBoostApplication = async () => {
    setButtonClicked('BoostApplication');
    try {
      dispatch(
        pushToGtmLoanAction({
          actionName: 'boost_application_button_pressed',
        })
      );
      dispatch(updateForm({ choseToBoostApplication: true }));
      await dispatch(patchApplication());
      closeModal('unique-selling-points-modal');
    } catch (error: any) {
      dispatch(
        pushToGtmLoanAction({
          stepStatus: StepStatus.Error,
          errorMessage: error.response?.data?.message,
        })
      );
    }
  };

  const onCompleteLaterClick = async () => {
    setButtonClicked('CompleteLater');
    dispatch(
      pushToGtmLoanAction({
        actionName: 'complete_later_button_pressed',
      })
    );
    dispatch(updateForm({ choseToBoostApplication: false }));
    await dispatch(patchApplication());
  };

  const openUSPModal = () => {
    openModal({
      title: t('Onboarding.CheckpointUSPPrompt'),
      id: 'unique-selling-points-modal',
      children: (
        <UniqueSellingPointsModal
          handleBoostApplication={handleBoostApplication}
        />
      ),
    });
  };

  const showIndicativeOfferLoadingIndicator =
    !skipIndicativeOfferFetch && (isFetching || !minLoadingTimeElapsed);

  return (
    <CheckpointPage>
      <CheckpointContent>
        <AnimatedWrapper spacing={isMobile ? 'sm' : 'xl'} align="start">
          <H5 data-record>{t('Onboarding.CheckpointTitle')}</H5>
          <Body size="lg" data-record>
            {t('Onboarding.CheckpointDescription')}
          </Body>
          {!isMobile && <UniqueSellingPoints />}
          {isMobile && (
            <Link onClick={openUSPModal} data-record>
              {t('Onboarding.CheckpointUSPPrompt')}
            </Link>
          )}
        </AnimatedWrapper>
        <img src="/qrayon20_small.png" alt="" />
        <img src="/image_transparent_man_002_small.png" alt="" />
        <ContentCard>
          {showIndicativeOfferLoadingIndicator ? (
            <LoadingAnimation
              component="img"
              src="/loading.gif"
              alt="Loading"
            />
          ) : (
            <>
              {data?.showIndicativeOffer ? (
                <IndicativeOffer
                  minAmount={data.minAmount}
                  maxAmount={data.maxAmount}
                />
              ) : (
                <VerticalTimeline />
              )}
            </>
          )}
        </ContentCard>
        <Stack align="center" pt={32}>
          <NextButton
            label="Onboarding.CheckpointImproveApplicationButtonText"
            isLoading={
              buttonClicked === 'BoostApplication' &&
              apiStatus.patch === ApiStatus.Started
            }
            action={handleBoostApplication}
            rightIcon="east"
            disabled={
              apiStatus.patch === ApiStatus.Started ||
              showIndicativeOfferLoadingIndicator
            }
          />
          {!showIndicativeOfferLoadingIndicator && (
            <SkipButton
              label="Onboarding.CompleteLater"
              action={onCompleteLaterClick}
              isLoading={
                buttonClicked === 'CompleteLater' &&
                apiStatus.patch === ApiStatus.Started
              }
              disabled={apiStatus.patch === ApiStatus.Started}
            />
          )}
        </Stack>
      </CheckpointContent>
    </CheckpointPage>
  );
};
